// ═══════════════════════════════════════════════════════════════
// signedUrlApi.js — Génération de signed URLs pour assets/documents
// Session Greg NEXT SUITE — 22 avril 2026
//
// Remplace le pattern ?token=JWT qui exposait le JWT admin 24h dans
// les URLs copiables. Signatures HMAC-SHA256 TTL 10min côté backend.
//
// Cache en mémoire + coalescence des requêtes identiques en vol
// (évite la tempête de requêtes si plusieurs <img> partagent un path).
// ═══════════════════════════════════════════════════════════════

const getToken = () => localStorage.getItem('oracle_token');

// Cache : path → { url, expiresAt (unix seconds) }
const cache = new Map();
// Requêtes en cours : path → Promise<string>
const inflight = new Map();

const SAFETY_MARGIN_SECONDS = 30; // re-sign si expire dans moins de 30s

function nowSec() {
    return Math.floor(Date.now() / 1000);
}

function isAssetPath(path) {
    return typeof path === 'string' && path.startsWith('/api/');
}

/**
 * Signe un path unique. Utilise cache + inflight coalescing.
 * @param {string} path - ex: '/api/assets/38' ou '/api/assets/38?download=1'
 * @param {number} ttl - durée de vie souhaitée en secondes (défaut 600)
 * @returns {Promise<string>} URL signée
 */
export async function signUrl(path, ttl = 600) {
    if (!isAssetPath(path)) {
        throw new Error(`signUrl: path doit commencer par /api/ (reçu: ${path})`);
    }
    const cached = cache.get(path);
    if (cached && cached.expiresAt - SAFETY_MARGIN_SECONDS > nowSec()) {
        return cached.url;
    }
    if (inflight.has(path)) return inflight.get(path);

    const promise = (async () => {
        const token = getToken();
        const res = await fetch('/api/sign-url', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ path, ttl }),
        });
        if (!res.ok) {
            const body = await res.text().catch(() => '');
            throw new Error(`sign-url ${res.status}: ${body.slice(0, 120)}`);
        }
        const data = await res.json();
        const expiresAt = nowSec() + (data.expires_in || ttl);
        cache.set(path, { url: data.url, expiresAt });
        return data.url;
    })();
    inflight.set(path, promise);
    try {
        return await promise;
    } finally {
        inflight.delete(path);
    }
}

/**
 * Signe plusieurs paths en une requête (anti tempête HTTP).
 * Utile pour Studio Images history, listes de fichiers, etc.
 * @param {string[]} paths
 * @param {number} ttl
 * @returns {Promise<Record<string, string>>} map path → url signée
 */
export async function signUrls(paths, ttl = 600) {
    if (!Array.isArray(paths) || paths.length === 0) return {};
    const validPaths = paths.filter(isAssetPath);
    const out = {};

    // Servir depuis le cache ce qui est encore frais
    const toFetch = [];
    for (const p of validPaths) {
        const cached = cache.get(p);
        if (cached && cached.expiresAt - SAFETY_MARGIN_SECONDS > nowSec()) {
            out[p] = cached.url;
        } else {
            toFetch.push(p);
        }
    }
    if (toFetch.length === 0) return out;

    const token = getToken();
    const res = await fetch('/api/sign-url/batch', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ paths: toFetch, ttl }),
    });
    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`sign-url/batch ${res.status}: ${body.slice(0, 120)}`);
    }
    const data = await res.json();
    const expiresAt = nowSec() + ttl;
    (data.urls || []).forEach((entry) => {
        if (entry.url) {
            cache.set(entry.path, { url: entry.url, expiresAt });
            out[entry.path] = entry.url;
        }
    });
    return out;
}

/**
 * Lookup synchrone du cache. Retourne l'URL signée si présente et fraîche,
 * sinon null. Utilisé pour init lazy de useState (évite le flash '' au remount).
 */
export function getCachedSignedUrl(path) {
    if (!path || !path.startsWith('/api/')) return null;
    const cached = cache.get(path);
    if (cached && cached.expiresAt - SAFETY_MARGIN_SECONDS > nowSec()) return cached.url;
    return null;
}

/** Invalide tout le cache (ex: après logout). */
export function clearSignedUrlCache() {
    cache.clear();
    inflight.clear();
}
